import React from 'react';
import { X } from 'lucide-react';
import { useConfigStore } from '../store/useConfigStore';
import { CurtainVisualizerCanvas } from './CurtainVisualizerCanvas';
import { VisualizerConfig } from './CurtainSceneController';
import { HeadingStyle, FabricType, HardwareStyle, MountType } from '../types/curtain';

export const CurtainVisualizerModal: React.FC = () => {
  const { 
    isVisualizerOpen, 
    setVisualizerOpen,
    styleConfig,
    updateStyleConfig,
    fabricConfig,
    updateFabricConfig,
    hardwareConfig,
    updateHardwareConfig,
    windowConfig,
    updateWindowConfig
  } = useConfigStore();

  React.useEffect(() => {
    if (!isVisualizerOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisualizerOpen(false);
    };
    
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisualizerOpen, setVisualizerOpen]);

  if (!isVisualizerOpen) return null;

  // Map store config to visualizer config
  const config: Omit<VisualizerConfig, 'openPercent' | 'mode'> = {
    style: (styleConfig.headingStyle as any) || 's-fold',
    width: (windowConfig.trackWidth || 2000) / 1000, // convert mm to meters roughly
    height: (windowConfig.finishedDrop || 2500) / 1000,
    fabricType: (fabricConfig.fabricType as any) || 'linen',
    hardwareStyle: (hardwareConfig.style as any) || 'architectural',
    mountType: (hardwareConfig.mountType as any) || 'wall',
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setVisualizerOpen(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex bg-gray-900" onClick={handleBackdropClick}>
      {/* Canvas Area */}
      <div className="flex-1 relative">
        <CurtainVisualizerCanvas config={config} />
      </div>

      {/* Sidebar Controls */}
      <div className="w-96 bg-white flex flex-col h-full shadow-2xl z-50">
        <div className="flex justify-between items-center p-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">3D Visualizer</h2>
          <button 
            onClick={() => setVisualizerOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8">
          
          {/* Heading Style */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Heading Style</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(HeadingStyle).map((style) => (
                <button
                  key={style}
                  onClick={() => updateStyleConfig({ headingStyle: style })}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border text-center transition-colors ${
                    styleConfig.headingStyle === style 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {style.replace('-', ' ')}
                </button>
              ))}
            </div>
          </section>

          {/* Fabric */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Fabric</h3>
            <div className="grid grid-cols-3 gap-2">
              {[FabricType.Linen, FabricType.Velvet, 'sheer'].map((type) => (
                <button
                  key={type}
                  onClick={() => updateFabricConfig({ fabricType: type as FabricType })}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border text-center capitalize transition-colors ${
                    fabricConfig.fabricType === type 
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </section>

          {/* Hardware */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Hardware</h3>
            <div className="flex flex-col gap-3">
              <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                {Object.values(HardwareStyle).map((style) => (
                  <button
                    key={style}
                    onClick={() => updateHardwareConfig({ style })}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${
                      hardwareConfig.style === style ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>
              
              <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                {Object.values(MountType).map((mount) => (
                  <button
                    key={mount}
                    onClick={() => updateHardwareConfig({ mountType: mount })}
                    className={`flex-1 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${
                      hardwareConfig.mountType === mount ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {mount} Mount
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Dimensions */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Dimensions</h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Width (mm)</span>
                  <span className="font-medium text-gray-900">{windowConfig.trackWidth || 2000}</span>
                </label>
                <input 
                  type="range" min="1000" max="5000" step="50"
                  value={windowConfig.trackWidth || 2000}
                  onChange={(e) => updateWindowConfig({ trackWidth: parseInt(e.target.value) })}
                  className="w-full accent-indigo-600"
                />
              </div>
              
              <div>
                <label className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Drop (mm)</span>
                  <span className="font-medium text-gray-900">{windowConfig.finishedDrop || 2500}</span>
                </label>
                <input 
                  type="range" min="1000" max="4000" step="50"
                  value={windowConfig.finishedDrop || 2500}
                  onChange={(e) => updateWindowConfig({ finishedDrop: parseInt(e.target.value) })}
                  className="w-full accent-indigo-600"
                />
              </div>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50">
          <button 
            onClick={() => setVisualizerOpen(false)}
            className="w-full py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors shadow-sm"
          >
            Apply & Return to Order
          </button>
        </div>
      </div>
    </div>
  );
};
